import { EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';

import { LoadingState, Logout } from '@personal-area/global/state';
import { Role } from '@personal-area/global/users/models';
import {
  Account,
  AccountCertificate,
  AccountPermissions,
  AccountStatus,
} from '@personal-area/global/accounts/models';

import { AccountActions } from '../actions/account';

export const filterAvailableForTrading = (accounts: Account[]): Account[] =>
  accounts.filter(
    (account) =>
      account.type === Role.Exchanger ||
      (account.login &&
        !account.isDemo &&
        account.status !== AccountStatus.archived &&
        account.actions.includes(AccountPermissions.deposit)),
  );

const accountsAdapter = createEntityAdapter<Account>();
export const { selectAll: getAccountsArray } =
  accountsAdapter.getSelectors();

export interface AccountState {
  accounts: EntityState<Account> & { loadingState: LoadingState };
  selected: 'all' | number | string;
  action: AccountPermissions | null;
  certificates: Record<number, AccountCertificate>;
}

const initialState: AccountState = {
  accounts: accountsAdapter.getInitialState({
    loadingState: LoadingState.initial,
  }),
  selected: 'all',
  action: null,
  certificates: {},
};
export const accountsReducer = createReducer(
  initialState,
  on(
    AccountActions.getAccounts,
    AccountActions.getAccount,
    (state) => ({
      ...state,
      accounts: {
        ...state.accounts,
        loadingState: LoadingState.loading,
      },
    })
  ),
  on(
    AccountActions.getAccountsFail,
    AccountActions.getAccountFail,
    (state) => ({
      ...state,
      accounts: {
        ...state.accounts,
        loadingState: LoadingState.failed,
      },
    }),
  ),
  on(
    AccountActions.getAccountsSuccess,
    (state, { accounts, primaryAccountId }) => ({
      ...state,
      accounts: accountsAdapter.setAll(accounts, {
        ...state.accounts,
        loadingState: LoadingState.loaded,
      }),
      selected: primaryAccountId || accounts[0]?.id || 'all',
    }),
  ),
  on(AccountActions.getAccountSuccess, (state, { account }) => ({
    ...state,
    accounts: accountsAdapter.setOne(account, {
      ...state.accounts,
      loadingState: LoadingState.loaded,
    }),
  })),
  on(AccountActions.selectAccount, (state, { accountId }) => ({
    ...state,
    selected: accountId,
  })),
  on(AccountActions.selectPartnerAccount, (state) => ({
    ...state,
    selected:
      getAccountsArray(state.accounts).find(({ type }) => type === Role.Partner)
        ?.id || state.selected,
  })),
  on(AccountActions.selectExchangerAccount, (state) => ({
    ...state,
    selected:
      getAccountsArray(state.accounts).find(
        ({ type }) => type === Role.Exchanger,
      )?.id || state.selected,
  })),
  on(AccountActions.selectFirstAvailableAccount, (state) => ({
    ...state,
    selected:
      filterAvailableForTrading(getAccountsArray(state.accounts)).slice(-1)?.[0]
        .id || state.selected,
  })),
  on(AccountActions.selectAction, (state, { action }) => ({
    ...state,
    action,
  })),
  on(AccountActions.updateAccount, (state, { update }) => ({
    ...state,
    accounts: accountsAdapter.updateOne(update, state.accounts),
  })),
  on(AccountActions.saveCertificate, (state, { id, certificate }) => ({
    ...state,
    certificates: {
      ...state.certificates,
      [id]: certificate,
    },
  })),
  on(AccountActions.removeCertificate, (state, { id }) => {
    const { [id]: _, ...restCertificates } = state.certificates;

    return {
      ...state,
      certificates: restCertificates,
    };
  }),
  on(Logout, state => initialState),
);
